import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type PortalStatus = 'active' | 'pending' | 'inactive' | 'no_account' | 'no_email'

type Person = {
  id: number
  first_name: string
  last_name: string
  preferred_name: string
  full_name: string
  date_of_birth: string | null
  personal_email: string
  mobile: string
  country_code: string
  employment_count: number
  current_employment_id: number | null
  user: number | null
  portal_status: PortalStatus
  created_at: string
  updated_at: string
}

type CreateInput = {
  first_name: string
  last_name: string
  preferred_name: string
  personal_email: string
  mobile: string
}

const EMPLOYMENT_FILTERS = ['ALL', 'EMPLOYED', 'NOT_EMPLOYED'] as const
type EmploymentFilter = typeof EMPLOYMENT_FILTERS[number]

export default function HRPeople() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<Person[]>('/hr/persons/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [employmentFilter, setEmploymentFilter] = useState<EmploymentFilter>('ALL')
  const [search, setSearch] = useState('')

  function openCreate() {
    setDraft(emptyDraft())
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Person>('/hr/persons/', {
      ...draft,
      // Sensible defaults for the rest of the Person fields — the record
      // page is where the user fills in identity, address, emergency
      // contact, photo, etc.
      date_of_birth: null,
      national_id: '',
      passport_number: '',
      tax_id: '',
      address_line1: '',
      city: '',
      postal_code: '',
      country_code: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      photo_url: '',
      user: null,
      notes: '',
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['hr', 'persons'] })
      toast.success('Person created')
      setCreating(false)
      navigate(`/admin/hr/people/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/hr/persons/${id}/`),
    onSuccess: () => {
      toast.success('Person deleted')
      qc.invalidateQueries({ queryKey: ['hr', 'persons'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const persons = personsQ.data || []
  const filtered = persons.filter((p) => {
    if (employmentFilter === 'EMPLOYED' && !p.current_employment_id) return false
    if (employmentFilter === 'NOT_EMPLOYED' && p.current_employment_id) return false
    if (search.trim()) {
      const needle = search.trim().toLowerCase()
      const hay = `${p.full_name} ${p.preferred_name} ${p.personal_email} ${p.mobile}`.toLowerCase()
      if (!hay.includes(needle)) return false
    }
    return true
  })

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">People</h2>
          <p className="text-sm text-muted-foreground">
            {persons.length} total · {persons.filter((p) => p.current_employment_id).length} currently employed
          </p>
        </div>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> New person</Button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
        <span className="text-muted-foreground">Filter:</span>
        {EMPLOYMENT_FILTERS.map((s) => (
          <button key={s} onClick={() => setEmploymentFilter(s)}
            className={`px-3 py-1 rounded ${employmentFilter === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' && 'All'}
            {s === 'EMPLOYED' && 'Currently employed'}
            {s === 'NOT_EMPLOYED' && 'Not employed'}
          </button>
        ))}
        <div className="ml-auto w-64">
          <Input
            placeholder="Search by name, email, phone…"
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
          />
        </div>
      </div>

      {personsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          {persons.length === 0
            ? 'No people yet. Use “New person” to add the first record.'
            : 'No people match this filter.'}
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Name</Th>
                <Th>Preferred</Th>
                <Th>Personal email</Th>
                <Th>Mobile</Th>
                <Th>Residence</Th>
                <Th>Employments</Th>
                <Th>Current</Th>
                <Th>Portal</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/hr/people/${p.id}`)}
                >
                  <Td>
                    <div className="font-medium text-foreground">{p.full_name}</div>
                  </Td>
                  <Td className="text-sm">
                    {p.preferred_name || <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td className="text-sm">
                    {p.personal_email || <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td className="text-sm">
                    {p.mobile || <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td className="text-xs font-mono uppercase">
                    {p.country_code || <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td className="text-xs">{p.employment_count}</Td>
                  <Td>
                    {p.current_employment_id ? (
                      <span className="text-xs px-2 py-0.5 rounded font-semibold bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded font-semibold bg-gray-100 text-gray-700">
                        None
                      </span>
                    )}
                  </Td>
                  <Td><PortalBadge status={p.portal_status} /></Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/hr/people/${p.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => confirm(
                            `${p.full_name} permanently`,
                            () => deleteMut.mutate(p.id),
                          )}
                          className="text-destructive"
                        >
                          Delete permanently
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New person"
        description="Create the identity record. Government IDs, address, emergency contact, and employment are filled in on the person's record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="p-first">First name *</FieldLabel>
              <Input id="p-first" required value={draft.first_name}
                onChange={(ev) => setDraft({ ...draft, first_name: ev.target.value })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="p-last">Last name *</FieldLabel>
              <Input id="p-last" required value={draft.last_name}
                onChange={(ev) => setDraft({ ...draft, last_name: ev.target.value })} />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="p-pref">Preferred name</FieldLabel>
            <Input id="p-pref" value={draft.preferred_name}
              onChange={(ev) => setDraft({ ...draft, preferred_name: ev.target.value })}
              placeholder="e.g. Sam (if different from legal first name)" />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="p-email">Personal email</FieldLabel>
              <Input id="p-email" type="email" value={draft.personal_email}
                onChange={(ev) => setDraft({ ...draft, personal_email: ev.target.value })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="p-mobile">Mobile</FieldLabel>
              <Input id="p-mobile" value={draft.mobile}
                onChange={(ev) => setDraft({ ...draft, mobile: ev.target.value })} />
            </Field>
          </div>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return { first_name: '', last_name: '', preferred_name: '', personal_email: '', mobile: '' }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}

/** Compact badge showing a person's portal account status at a glance. */
function PortalBadge({ status }: { status: PortalStatus | undefined }) {
  if (!status || status === 'no_email') {
    return <span className="text-xs text-muted-foreground/60" title="No email address — cannot auto-provision">—</span>
  }
  const cfg: Record<PortalStatus, { label: string; cls: string; tip: string }> = {
    active:     { label: 'Active',      cls: 'bg-green-100 text-green-800',  tip: 'User account active — portal access granted' },
    pending:    { label: 'Invite sent', cls: 'bg-amber-100 text-amber-800',  tip: 'Welcome email sent — waiting for password to be set' },
    inactive:   { label: 'Inactive',    cls: 'bg-red-100 text-red-700',      tip: 'User account deactivated — no active employment or engagement' },
    no_account: { label: 'No account',  cls: 'bg-gray-100 text-gray-600',   tip: 'Email known but no user account created yet' },
    no_email:   { label: '—',           cls: '',                             tip: '' },
  }
  const { label, cls, tip } = cfg[status]
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded font-semibold ${cls}`}
      title={tip}
    >
      {label}
    </span>
  )
}
